/**
 * Utilities for determining page information for table rows
 */
// Import utility to extract original indices from row IDs
import { extractOriginalIndexFromId } from "./pdfTableViewerUtils";

/**
 * Get the page number for a specific row in the table
 * @param {Object} selectedTable - The selected table object
 * @param {number} rowIndex - The row index (0-based for headers, 1+ for data rows)
 * @returns {number|null} - Page number or null if not found
 */
export const getRowPage = (selectedTable, rowIndex) => {
  if (!selectedTable) return null;

  // Look for any cell in this row to get page information
  const cellKeys = Object.keys(selectedTable).filter((key) =>
    key.match(/^\d+_\d+$/)
  );

  for (const cellKey of cellKeys) {
    const [rowStr] = cellKey.split("_");
    const cellRowIndex = parseInt(rowStr, 10);

    if (cellRowIndex === rowIndex) {
      const cellData = selectedTable[cellKey];
      if (
        cellData &&
        Array.isArray(cellData) &&
        cellData[0] &&
        cellData[0].page
      ) {
        return cellData[0].page;
      }
    }
  }

  return null;
};

/**
 * Get page information for all rows in the table
 * @param {Object} selectedTable - The selected table object
 * @param {Object} tableData - The processed table data with headers and rows
 * @returns {Object} - Map of row index to page number
 */
export const getRowPageMap = (selectedTable, tableData) => {
  if (!selectedTable || !tableData) return {};

  const pageMap = {};

  // Get page for headers (row 0)
  pageMap[0] = getRowPage(selectedTable, 0);

  // Get page for each data row
  if (tableData.rows) {
    tableData.rows.forEach((_, rowIndex) => {
      const dataRowIndex = rowIndex + 1; // Data rows start at index 1
      pageMap[dataRowIndex] = getRowPage(selectedTable, dataRowIndex);
    });
  }

  return pageMap;
};

/**
 * Check if the table has rows spanning multiple pages
 * @param {Object} pageMap - Map of row index to page number from getRowPageMap
 * @returns {boolean} - True if rows span multiple pages
 */
export const hasMultipleRowPages = (pageMap) => {
  const pages = new Set(Object.values(pageMap).filter((page) => page !== null));
  return pages.size > 1;
};

/**
 * Calculate page groups with rowspan information for merged cells
 * @param {Object} pageMap - Map of row index to page number
 * @param {number} totalDataRows - Total number of data rows
 * @returns {Object} - Map of row index to page group info {page, rowspan, isFirst}
 */
export const getPageGroups = (pageMap, totalDataRows) => {
  const groups = {};
  const dataRowIndexes = Array.from({ length: totalDataRows }, (_, i) => i + 1);

  let currentPage = null;
  let currentGroupStart = null;
  let currentGroupSize = 0;

  dataRowIndexes.forEach((rowIndex, arrayIndex) => {
    const rowPage = pageMap[rowIndex];

    if (rowPage !== currentPage) {
      // Finish previous group
      if (currentPage !== null && currentGroupStart !== null) {
        // Mark the first row of the previous group
        groups[currentGroupStart] = {
          page: currentPage,
          rowspan: currentGroupSize,
          isFirst: true,
        };

        // Mark remaining rows in the group as non-first
        for (
          let i = currentGroupStart + 1;
          i < currentGroupStart + currentGroupSize;
          i++
        ) {
          groups[i] = {
            page: currentPage,
            rowspan: 0,
            isFirst: false,
          };
        }
      }

      // Start new group
      currentPage = rowPage;
      currentGroupStart = rowIndex;
      currentGroupSize = 1;
    } else {
      // Continue current group
      currentGroupSize++;
    }

    // Handle last group
    if (
      arrayIndex === dataRowIndexes.length - 1 &&
      currentPage !== null &&
      currentGroupStart !== null
    ) {
      groups[currentGroupStart] = {
        page: currentPage,
        rowspan: currentGroupSize,
        isFirst: true,
      };

      for (
        let i = currentGroupStart + 1;
        i < currentGroupStart + currentGroupSize;
        i++
      ) {
        groups[i] = {
          page: currentPage,
          rowspan: 0,
          isFirst: false,
        };
      }
    }
  });

  return groups;
};

/**
 * Get page information for all VISIBLE rows in the table (accounting for deleted/recovered rows)
 * This function automatically handles:
 * - Row deletions: Only shows page info for currently visible rows
 * - Row recovery: Shows updated page info when rows are restored
 * @param {Object} selectedTable - The selected table object
 * @param {Object} tableData - The processed table data with headers and rows
 * @param {Array} rowIds - Array of current row IDs (reflects current order after deletions/recoveries)
 * @returns {Object} - Map of visual row index to page number
 */
export const getVisibleRowPageMap = (selectedTable, tableData, rowIds) => {
  if (!selectedTable || !tableData || !rowIds) return {};

  const pageMap = {};

  // Get page for headers (row 0)
  pageMap[0] = getRowPage(selectedTable, 0);

  // Get page for each visible data row using rowIds to get original indices
  if (tableData.rows && rowIds) {
    rowIds.forEach((rowId, visualIndex) => {
      const originalRowIndex = extractOriginalIndexFromId(rowId);
      if (originalRowIndex >= 0) {
        // Visual index starts at 1 for data rows (0 is header)
        const visualRowIndex = visualIndex + 1;
        // Use original row index + 1 to account for header row in selectedTable keys
        const page = getRowPage(selectedTable, originalRowIndex + 1);
        if (page !== null) {
          pageMap[visualRowIndex] = page;
        }
      }
    });
  }

  return pageMap;
};

/**
 * Calculate page groups for visible rows (accounting for deleted/recovered rows)
 * Automatically recalculates merged cells when rows are deleted or recovered
 * @param {Object} pageMap - Map of visual row index to page number
 * @param {number} totalVisibleRows - Total number of visible data rows
 * @returns {Object} - Map of visual row index to page group info {page, rowspan, isFirst}
 */
export const getVisiblePageGroups = (pageMap, totalVisibleRows) => {
  const groups = {};
  const dataRowIndexes = Array.from(
    { length: totalVisibleRows },
    (_, i) => i + 1
  );

  let currentPage = null;
  let currentGroupStart = null;
  let currentGroupSize = 0;

  dataRowIndexes.forEach((visualRowIndex, arrayIndex) => {
    const rowPage = pageMap[visualRowIndex];

    if (rowPage !== currentPage) {
      // Finish previous group
      if (currentPage !== null && currentGroupStart !== null) {
        // Mark the first row of the previous group
        groups[currentGroupStart] = {
          page: currentPage,
          rowspan: currentGroupSize,
          isFirst: true,
        };

        // Mark remaining rows in the group as non-first
        for (
          let i = currentGroupStart + 1;
          i < currentGroupStart + currentGroupSize;
          i++
        ) {
          groups[i] = {
            page: currentPage,
            rowspan: 0,
            isFirst: false,
          };
        }
      }

      // Start new group
      currentPage = rowPage;
      currentGroupStart = visualRowIndex;
      currentGroupSize = 1;
    } else {
      // Continue current group
      currentGroupSize++;
    }

    // Handle last group
    if (
      arrayIndex === dataRowIndexes.length - 1 &&
      currentPage !== null &&
      currentGroupStart !== null
    ) {
      groups[currentGroupStart] = {
        page: currentPage,
        rowspan: currentGroupSize,
        isFirst: true,
      };

      for (
        let i = currentGroupStart + 1;
        i < currentGroupStart + currentGroupSize;
        i++
      ) {
        groups[i] = {
          page: currentPage,
          rowspan: 0,
          isFirst: false,
        };
      }
    }
  });

  return groups;
};
